import React from 'react';
import { Card, Row, Col, Typography, Avatar, Spin, Tag, Button, Badge } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ShopOutlined, SwapOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { Business, BusinessType } from '../../types/business';
import { useNauvatekSupportUnreadCount } from '../../hooks/useSupportTickets';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { PublicLanguageSwitcher } from '../../components/PublicLanguageSwitcher';
import { getErrorMessage } from '../../utils/i18n/getErrorMessage';
import {
  peekPendingNotifDeepLink,
  resolvePendingNavigationPath,
  clearPendingNotifDeepLink,
} from '../../utils/notificationDeepLink';

const { Title, Text } = Typography;

const COLORS = {
  blue: '#0A3780',
  lightBlue: '#1B4F96',
  gray: '#666666',
  lightGray: '#F5F5F5',
  white: '#FFFFFF',
  border: '#E0E0E0',
};

const NauvatekLogo: React.FC<{ size?: number }> = ({ size = 150 }) => (
  <img
    src="/LOGO.png"
    alt="NAUVATEK Logo"
    style={{ width: size, height: size, objectFit: 'contain' }}
    onError={(e) => {
      e.currentTarget.style.display = 'none';
    }}
  />
);

function businessTypeLabel(type: BusinessType | undefined, t: (key: string) => string): string {
  if (!type) return t('businessTypes.fallback');
  const key = `businessTypes.${type}`;
  const label = t(key);
  return label === key ? t('businessTypes.fallback') : label;
}

interface BusinessCardProps {
  business: Business & { description?: string };
  onSelect: (businessId: string) => void;
  isSelected?: boolean;
  disabled?: boolean;
}

const BusinessCard: React.FC<BusinessCardProps> = ({ business, onSelect, isSelected, disabled }) => {
  const { t } = useAppTranslation('common');
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    onSelect(business.id);
  };

  const isConfig = !!business.configurationInProgress;
  const isAccessible = !disabled;
  const isPrimary = isAccessible && !isConfig;

  return (
    <Card
      hoverable={!disabled}
      onClick={handleClick}
      style={{
        minHeight: 96,
        border: isSelected ? `2px solid ${COLORS.blue}` : `1px solid ${COLORS.border}`,
        borderRadius: 12,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s ease',
        background: disabled ? '#fafafa' : '#fff',
        position: 'relative',
        zIndex: 1,
      }}
      bodyStyle={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 16,
        pointerEvents: 'auto',
      }}
    >
      <div style={{ marginRight: 16, display: 'flex', alignItems: 'center' }}>
        <Avatar
          size={48}
          icon={<ShopOutlined />}
          style={{
            backgroundColor: disabled ? '#f0f0f0' : isPrimary ? '#eb2f96' : '#f5f5f5',
            color: disabled ? '#bfbfbf' : isPrimary ? '#ffffff' : '#666666',
          }}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <Title
          level={4}
          style={{
            margin: 0,
            marginBottom: 4,
            color: isPrimary ? COLORS.blue : disabled ? '#999999' : '#262626',
            fontSize: 16,
          }}
        >
          {business.name}
        </Title>
        <Text
          style={{
            fontSize: 13,
            color: isPrimary ? COLORS.blue : disabled ? '#b3b3b3' : '#595959',
          }}
        >
          {businessTypeLabel(business.type, t)}
        </Text>
        {business.description && (
          <div style={{ marginTop: 4 }}>
            <Text
              style={{
                display: 'block',
                fontSize: 12,
                color: isPrimary ? COLORS.blue : disabled ? '#c0c0c0' : '#8c8c8c',
              }}
            >
              {business.description}
            </Text>
          </div>
        )}
      </div>

      <div style={{ marginLeft: 16 }}>
        {business.configurationInProgress && (
          <Tag color="orange" style={{ fontSize: 11 }}>
            {t('businessSelection.configInProgress')}
          </Tag>
        )}
      </div>
    </Card>
  );
};

const BusinessSelection: React.FC = () => {
  const { t } = useAppTranslation('common');
  const { availableBusinesses, currentBusiness, selectBusiness, userProfile, isSuperAdmin, isCSM } =
    useAuth();
  const { data: supportUnreadCount = 0 } = useNauvatekSupportUnreadCount({
    enabled: isSuperAdmin || isCSM,
  });
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const leftPanelMessage = React.useMemo(() => {
    if (isSuperAdmin) {
      return t('businessSelection.helloSuperAdmin', {
        name: userProfile?.name || t('businessSelection.roleSuperAdmin'),
      });
    }
    if (isCSM) {
      return t('businessSelection.helloCsm', {
        name: userProfile?.name || t('businessSelection.roleCsm'),
      });
    }
    return t('businessSelection.helloUser', {
      name: userProfile?.name || '',
    });
  }, [userProfile, isSuperAdmin, isCSM, t]);

  const handleBusinessSelect = async (businessId: string) => {
    const pending = peekPendingNotifDeepLink();
    const afterSelectPath = pending
      ? resolvePendingNavigationPath(pending)
      : '/start';

    if (businessId === currentBusiness?.id) {
      if (pending) clearPendingNotifDeepLink();
      navigate(afterSelectPath);
      return;
    }

    setLoading(true);
    try {
      await selectBusiness(businessId);
      if (pending) clearPendingNotifDeepLink();
      navigate(afterSelectPath);
    } catch (error: unknown) {
      console.error('Erreur lors de la sélection du business:', error);
      alert(getErrorMessage(error, t) || t('businessSelection.selectError'));
    } finally {
      setLoading(false);
    }
  };

  if (!availableBusinesses || availableBusinesses.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          background: COLORS.lightGray,
        }}
      >
        <div style={{ position: 'absolute', top: 16, right: 16 }}>
          <PublicLanguageSwitcher />
        </div>
        <Spin size="large" />
        <Text style={{ marginTop: 16, color: COLORS.gray }}>
          {loading ? t('businessSelection.loading') : t('businessSelection.empty')}
        </Text>
        {userProfile && userProfile.businessIds && userProfile.businessIds.length > 0 && (
          <Text type="secondary" style={{ marginTop: 8 }}>
            Business IDs: {userProfile.businessIds.join(', ')}
          </Text>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        background: COLORS.lightGray,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          flex: isMobile ? 'none' : 1,
          height: isMobile ? 'auto' : '100vh',
          background: COLORS.blue,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: isMobile ? '32px 20px 24px 20px' : 20,
          overflow: 'auto',
        }}
      >
        <div style={{ textAlign: 'center', color: COLORS.white }}>
          <div style={{ marginBottom: isMobile ? 16 : 32 }}>
            <NauvatekLogo size={isMobile ? 80 : 220} />
          </div>

          {!isMobile && (
            <div style={{ marginTop: 32, maxWidth: 340 }}>
              <Text
                style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: 16,
                  lineHeight: 1.6,
                  display: 'block',
                }}
              >
                {leftPanelMessage}
              </Text>
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: isMobile ? 'flex-start' : 'center',
          padding: isMobile ? 20 : 32,
          background: isMobile
            ? COLORS.white
            : `
            linear-gradient(135deg, 
              ${COLORS.white} 0%, 
              #fafbfc 50%, 
              ${COLORS.lightGray} 100%
            )
          `,
          height: isMobile ? 'auto' : '100vh',
          overflow: 'auto',
          paddingTop: 20,
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 2,
          }}
        >
          <PublicLanguageSwitcher />
        </div>
        {!isMobile && (
          <>
            <div
              style={{
                position: 'absolute',
                top: '10%',
                right: '8%',
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: `linear-gradient(45deg, ${COLORS.blue}20, ${COLORS.blue}35)`,
                animation: 'float 6s ease-in-out infinite',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '60%',
                right: '15%',
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                background: `linear-gradient(45deg, ${COLORS.blue}15, ${COLORS.blue}28)`,
                animation: 'float 8s ease-in-out infinite reverse',
              }}
            />
            <style>
              {`
              @keyframes float {
                0%, 100% { transform: translateY(0px); opacity: 0.7; }
                50% { transform: translateY(-15px); opacity: 1; }
              }
            `}
            </style>
          </>
        )}
        <div
          style={{
            width: '100%',
            maxWidth: 900,
          }}
        >
          <div style={{ marginBottom: 24, textAlign: 'center' }}>
            <Title level={isMobile ? 3 : 2} style={{ marginBottom: 8, color: COLORS.blue }}>
              <SwapOutlined style={{ marginRight: 12 }} />
              {t('businessSelection.title')}
            </Title>
            <Text style={{ color: COLORS.gray, fontSize: 14 }}>
              {t('businessSelection.subtitle')}
            </Text>
            {(isSuperAdmin || isCSM) && (
              <div style={{ marginTop: 16 }}>
                <Badge count={supportUnreadCount} offset={[-4, 4]}>
                  <Button
                    type="primary"
                    icon={<CustomerServiceOutlined />}
                    onClick={() => navigate('/super-admin/support-tickets')}
                    style={{ background: '#E91E63', borderColor: '#E91E63' }}
                  >
                    {t('businessSelection.supportClients')}
                  </Button>
                </Badge>
              </div>
            )}
          </div>

          <Row gutter={[16, 16]} justify="center">
            {availableBusinesses.map((business) => {
              const canBypassConfigLock = isSuperAdmin || isCSM;
              const disabled = business.configurationInProgress && !canBypassConfigLock;
              return (
                <Col xs={24} sm={24} md={24} key={business.id}>
                  <BusinessCard
                    business={business}
                    onSelect={handleBusinessSelect}
                    isSelected={currentBusiness?.id === business.id}
                    disabled={disabled}
                  />
                </Col>
              );
            })}
          </Row>

          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('businessSelection.footerHint')}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessSelection;
